import { createContext, useCallback, useContext, useMemo } from "react";
import { AuthManager, useAuthManager } from "../hooks/useAuthManager";
import { useDispatch } from "react-redux";
import { authenticateTwoFactor, setCurrentUser, setIsAuthLoading, setIsProfileLoading } from "../authSlice";
import { clearAllToastMessages, isPAPLUsers } from "utils/helpers";
import { apiV1 } from "api-v2";
import { User } from "../types/User.type";
import { navigate } from "@reach/router";
import { getOrFetchXsrfToken } from "../utils";
import { logErrorIfDebug } from "utils/logging";
import { NavigationPathsBase, useNavigationPaths } from "../hooks/useNavigationPaths";
import { useAppMenu } from "newui/features/AppMenu/hooks/useAppMenu";

export interface AuthenticateValues {
	[key: string]: unknown;
	email: string;
	invitetoken?: string | null;
	password: string;
	remember_me?: boolean;
}

export interface AuthenticateResponse {
	company_admin: boolean;
	expires_in: number;
	is_admin: boolean;
	success: boolean;
	token_type: string;
	user: User;
}

interface AuthContextValue extends AuthManager, NavigationPathsBase {
	authenticateUser: (
		authData: AuthenticateResponse
	) => Promise<AuthenticateResponse>;
	loginUser: (values: AuthenticateValues) => Promise<AuthenticateResponse>;
}

export const AuthContext = createContext<AuthContextValue>(null);

export const AuthContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { errorMessage, isSuspended, isVerified, logoutUser } = useAuthManager();
	const { clientPaths, currentUserHomePage, guestPaths, supplierPaths } = useNavigationPaths();
	const { collapse } = useAppMenu();

	const dispatch = useDispatch();

	const authenticateUser = useCallback(
		async (authData: AuthenticateResponse): Promise<AuthenticateResponse> => {
			try {
				let user = authData?.user;

				if (!user) {
					throw new Error("User data is missing after authentication");
				}

				// authenticate user in store
				await dispatch(
					authenticateTwoFactor({
						two_factor_enabled: user?.two_factor_enabled,
						two_factor_verified: false,
					})
				);

				/* -------------------------------------------------------------- */
				/* Two-factor enabled                                             */
				/* -------------------------------------------------------------- */

				if (user?.two_factor_enabled) {
					localStorage.setItem("two_factor_enabled", "true");
					localStorage.setItem("two_factor_verified", "false");

					navigate("/two-factor");
					return authData;
				}

				/* -------------------------------------------------------------- */
				/* No Two-factor                                                 */
				/* -------------------------------------------------------------- */

				localStorage.setItem("two_factor_enabled", "false");
				localStorage.setItem("two_factor_verified", "false");

				if (isPAPLUsers(user)) {
					navigate("/two-factor-setup");
					return authData;
				}

				if (!user.company && !user.is_guest) {
					const userProfileResponse = await apiV1.get("auth/user-profile");
					user = userProfileResponse?.data?.data;
				}

				collapse(false);

				dispatch(setCurrentUser(user));

				return authData;
			} catch (error) {
				logErrorIfDebug("Authentication failed:", error);
				throw error;
			}
		},
		[collapse, dispatch]
	);

	const loginUser = useCallback(
		async (values: AuthenticateValues): Promise<AuthenticateResponse> => {
			try {
				await getOrFetchXsrfToken();
				const userResponse = await apiV1.post<AuthenticateResponse>(
					"auth/login",
					{
						...values,
					}
				);

				if (!userResponse.data?.success) {
					userResponse.data;
				}

				clearAllToastMessages();
				dispatch(setIsProfileLoading(true));
				dispatch(setIsAuthLoading(true));
				return await authenticateUser(userResponse.data);
			} catch (error) {
				console.error("Login failed:", error);
				dispatch(setIsProfileLoading(false));
				throw error;
			} finally {
				dispatch(setIsAuthLoading(false));
			}
		},
		[authenticateUser, dispatch]
	);

	const contextValue = useMemo(() => ({
		clientPaths,
		guestPaths,
		currentUserHomePage,
		supplierPaths,
		errorMessage,
		isSuspended,
		isVerified,
		authenticateUser,
		loginUser,
		logoutUser
	}), [
		clientPaths,
		guestPaths,
		currentUserHomePage,
		supplierPaths,
		errorMessage,
		isSuspended,
		isVerified,
		authenticateUser,
		loginUser,
		logoutUser
	]);

	return (
		<AuthContext.Provider
			value={contextValue}>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuthContext = (): AuthContextValue => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuthContext must be used within AuthContextProvider");
	}
	return context;
};
